
// src/components/Footer.js
import {
  FacebookIcon,
  FileText,
  Globe,
  Home,
  InstagramIcon,
  LinkedinIcon,
  MailIcon,
  MapPin,
  MessageCircle,
  Package,
  PhoneIcon,
  Twitter,
  Users,
  Youtube
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import pitechLogo from '../assets/pitech-logo.png';
import axios from "../utils/axiosHandler";

// Social media icon mapping
const socialIconMap = {
  instagram: InstagramIcon,
  facebook: FacebookIcon,
  linkedin: LinkedinIcon,
  twitter: Twitter,
  youtube: Youtube
};

const Footer = ({ menuName = 'footer' }) => {
  const [menuData, setMenuData] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [menuLoading, setMenuLoading] = useState(true);
  const [footerOptions, setFooterOptions] = useState({});
  const [customStyles, setCustomStyles] = useState({});
  const location = useLocation();
  const isAdminDashboard = location.pathname === '/pitech';

  useEffect(() => {
    const fixAdminDashboardPadding = () => {
      // Fix for admin dashboard
      if (isAdminDashboard) {

        const footer = document.querySelector('footer');
        if (footer) {
          footer.style.display = 'none'; // Fixed height for navbar on admin dashboard
        }
      }
    };
    fixAdminDashboardPadding();
  });

  // Fetch menu data
  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const response = await axios.get(`/api/menus/name/${menuName}`);
        const menu = response.data;

        setMenuData(menu);
        // Sort menu items by order property before setting state
        if (menu.items && menu.items.length > 0) {
          const sortedItems = [...menu.items].sort((a, b) => {
            // If order properties are the same, maintain original order
            if (a.order === b.order) return 0;
            // Otherwise sort by order property
            return a.order - b.order;
          });

          // Also sort children for each item
          sortedItems.forEach(item => {
            if (item.children && item.children.length > 0) {
              item.children.sort((a, b) => a.order - b.order);
            }
          });

          setMenuItems(sortedItems);
        } else {
          setMenuItems([]);
        }

        // Extract styling information
        if (menu.styles) {
          setCustomStyles(menu.styles);
        }


        if (menu.footerOptions) {
          setFooterOptions(menu.footerOptions);
        }

        // Load custom CSS if provided
        if (menu.customCss) {
          // Create a style element for custom CSS
          const styleElement = document.createElement('style');
          styleElement.id = `footer-custom-css-${menu._id}`;
          styleElement.textContent = menu.customCss;
          document.head.appendChild(styleElement);

          // Cleanup function
          return () => {
            const existingStyle = document.getElementById(`footer-custom-css-${menu._id}`);
            if (existingStyle) {
              document.head.removeChild(existingStyle);
            }
          };
        }
      } catch (error) {
        console.error('Error fetching footer menu:', error);
        // If we can't fetch the footer menu, we'll use default values
        setMenuLoading(false);
      } finally {
        setMenuLoading(false);
      }
    };

    fetchMenu();
  }, [menuName]);

  // Generate footer style
  const getFooterStyle = () => {
    const styles = {};

    if (customStyles.backgroundColor) {
      styles.backgroundColor = customStyles.backgroundColor;
    }

    if (customStyles.textColor) {
      styles.color = customStyles.textColor;
    }

    if (customStyles.fontFamily) {
      styles.fontFamily = customStyles.fontFamily;
    }

    return styles;
  };

  // Generate link style
  const getLinkStyle = (isHovered = false) => {
    const styles = {};

    if (isHovered && customStyles.hoverTextColor) {
      styles.color = customStyles.hoverTextColor;
    } else if (customStyles.textColor) {
      styles.color = customStyles.textColor;
    }

    return styles;
  };

  // Group menu items by parent for multi-column layout
  const getGroupedMenuItems = () => {
    // Create an appropriate number of columns based on settings
    const numColumns = footerOptions.columns || 4;
    const columns = Array(numColumns).fill().map(() => []);

    // Only get top-level items
    const topLevelItems = menuItems.filter(item => !item.parent);

    // Distribute items across columns evenly
    topLevelItems.forEach((item, index) => {
      const columnIndex = index % numColumns;
      columns[columnIndex].push(item);
    });

    return columns;
  };

  // Generate copyright text with current year
  const getCopyrightText = () => {
    const currentYear = new Date().getFullYear();
    const text = footerOptions.copyrightText || '© 2025 Company Name | All Rights Reserved';
    return text.replace('2025', currentYear);
  };
  // If still loading, show minimal footer
  if (menuLoading) {
    return (
      <footer className="bg-gray-900 text-white py-6">
        <div className="container mx-auto px-4 text-center">
          <p>Loading footer...</p>
        </div>
      </footer>
    );
  }

  // Define layout based on footer options
  const getFooterLayout = () => {
    const layout = footerOptions.layout || 'standard';

    switch (layout) {
      case 'centered':
        return (
          <div className="text-center">
            {footerOptions.logo && (
              <div className="mb-6">
                <img
                  src={footerOptions.logo}
                  alt={footerOptions.companyName || 'Company Logo'}
                  className="mx-auto"
                  style={{
                    height: footerOptions.logoHeight || '60px',
                    width: footerOptions.logoWidth || 'auto'
                  }}
                />
              </div>
            )}

            {footerOptions.companyName && (
              <h4 className="text-xl font-bold mb-2">{footerOptions.companyName}</h4>
            )}

            {footerOptions.companyDescription && (
              <p className="text-gray-400 mb-6 max-w-lg mx-auto">{footerOptions.companyDescription}</p>
            )}

            {footerOptions.showQuickLinks !== false && menuItems.length > 0 && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
                <div className="flex flex-wrap justify-center gap-4">
                  {menuItems.map(item => (
                    <Link key={item._id} to={item.path}
                    //  className="hover:text-blue-500"
                    >
                      {item.title}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {footerOptions.showContactInfo !== false && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold mb-4">Contact Us</h4>
                {footerOptions.contactPhone && (
                  <p className="flex items-center justify-center">
                    <PhoneIcon className="mr-2 h-5 w-5" />
                    <a href={`tel:${footerOptions.contactPhone}`}>{footerOptions.contactPhone}</a>
                  </p>
                )}
                {footerOptions.contactEmail && (
                  <p className="flex items-center justify-center mt-2">
                    <MailIcon className="mr-2 h-5 w-5" />
                    <a href={`mailto:${footerOptions.contactEmail}`}>{footerOptions.contactEmail}</a>
                  </p>
                )}
                {footerOptions.contactAddress && (
                  <p className="flex items-center justify-center mt-2">
                    <MapPin className="mr-2 h-5 w-5" />
                    {footerOptions.contactAddress}
                  </p>
                )}
              </div>
            )}

            {footerOptions.showSocialIcons !== false && footerOptions.socialLinks && footerOptions.socialLinks.length > 0 && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold mb-4">Connect With Us</h4>
                <div className="flex justify-center space-x-4">
                  {footerOptions.socialLinks.map((social, index) => {
                    const SocialIcon = socialIconMap[social.platform] || Globe;
                    return (
                      <a
                        key={index}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-white"
                      >
                        <SocialIcon className="h-6 w-6" />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );

      case 'minimal':
        return (
          <div className="text-center py-4">
            {footerOptions.logo && (
              <div className="mb-4">
                <img
                  src={footerOptions.logo}
                  alt={footerOptions.companyName || 'Company Logo'}
                  className="mx-auto"
                  style={{
                    height: '60px',
                    width: 'auto'
                  }}
                />
              </div>
            )}

            {footerOptions.showSocialIcons !== false && footerOptions.socialLinks && footerOptions.socialLinks.length > 0 && (
              <div className="flex justify-center space-x-4 mb-4">
                {footerOptions.socialLinks.map((social, index) => {
                  const SocialIcon = socialIconMap[social.platform] || Globe;
                  return (
                    <a
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white"
                    >
                      <SocialIcon className="h-5 w-5" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        );

      default: // standard
        const columns = getGroupedMenuItems();

        return (
          <div className={`grid md:grid-cols-${footerOptions.columns || 4} gap-8`}>
            {/* Company Info Column */}
            <div className="px-8">
              <h4 className="text-xl font-bold mb-4">
                {footerOptions.companyName || 'Company Name'}
              </h4>

              {footerOptions.companyDescription && (
                <p className="text-gray-400">
                  {footerOptions.companyDescription}
                </p>
              )}

              <div className="mt-4">
                <Link to="/privacy-policy" className="hover:text-white transition">
                  Privacy Policy
                </Link>
                <span> &nbsp;&nbsp; - &nbsp;&nbsp; </span>
                <Link to="/terms-conditions" className="hover:text-white transition">
                  Terms & Conditions
                </Link>
              </div>

              {footerOptions.logo && (
                <div className="mt-4">
                  <img
                    src={footerOptions.logo}
                    alt={footerOptions.companyName || 'Company Logo'}
                    style={{
                      height: footerOptions.logoHeight || '60px',
                      width: footerOptions.logoWidth || 'auto'
                    }}
                  />
                </div>
              )}

            </div>

            {/* Menu Item Columns */}
            {footerOptions.showQuickLinks !== false && menuItems.length > 0 && (
              <div className="px-8">
                <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
                <div className="flex flex-col justify-center gap-1">
                  {menuItems.map(item => (
                    <Link key={item._id} to={item.path}
                    // className="hover:text-blue-500"
                    >
                      {item.title}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Show Contact Info Column */}
            {footerOptions.showContactInfo !== false && (
              <div className="px-8">
                <h4 className="text-xl font-bold mb-4">Contact</h4>
                {footerOptions.contactPhone && (
                  <>
                    <div className="flex items-center ">
                      <PhoneIcon className="mr-2 h-5 w-5" />

                      <div className="flex flex-col gap-1">
                        <a href={`tel:${footerOptions.contactPhone}`}>{footerOptions.contactPhone}</a>
                        <p className="">
                          <a href={`tel:7787585859`} >+1 (778) 758-5859</a>
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center my-3">
                      <a href="https://wa.me/17787585859" className="text-md text-white font-semibold text-blue-600 flex items-center justify-center gap-3">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="inline-block text-green-500"
                        >
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                        </svg>
                        WhatsApp
                      </a>
                    </div>
                  </>
                )}
                {footerOptions.contactEmail && (
                  <p className="flex items-center mt-2">
                    <MailIcon className="mr-2 h-5 w-5" />
                    <a href={`mailto:${footerOptions.contactEmail}`}>{footerOptions.contactEmail}</a>
                  </p>
                )}
                {footerOptions.contactAddress && (
                  <p className="flex items-center mt-2">
                    <MapPin className="mr-2 h-5 w-5" />
                    {footerOptions.contactAddress}
                  </p>
                )}
              </div>
            )}

            {/* Show Social Links Column */}
            {footerOptions.showSocialIcons !== false && footerOptions.socialLinks && footerOptions.socialLinks.length > 0 && (
              <div className="px-8">
                <h4 className="text-xl font-bold mb-4">Follow Us</h4>
                <div className="flex space-x-4">
                  {footerOptions.socialLinks.map((social, index) => {
                    const SocialIcon = socialIconMap[social.platform] || Globe;
                    return (
                      <a
                        key={index}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-white"
                      >
                        <SocialIcon className="h-5 w-5" />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
    }
  };

  // Function to render icon based on name
  const renderIcon = (iconName) => {
    const iconMap = {
      Home: Home,
      Package: Package,
      Mail: MailIcon,
      Users: Users,
      FileText: FileText,
      MessageCircle: MessageCircle
    };

    if (!iconName) return null;

    const IconComponent = iconMap[iconName];
    if (IconComponent) {
      return <IconComponent className="w-4 h-4 mr-1" />;
    }
    return null;
  };

  return (
    <footer className="bg-gray-900 text-white py-12 pb-0 mb-0" style={getFooterStyle()}>
      <div className="container mx-auto px-4">
        {getFooterLayout()}

        {/* Powered By Attribution */}
        <div className="mt-8 border-t border-gray-800 pt-4 pb-6">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center text-center md:text-left">
              <p className="mb-3 md:mb-0">{getCopyrightText()}</p>

              {footerOptions.poweredBy?.show !== false && (
                <div className="flex items-center justify-center md:justify-end">
                  <a
                    href={footerOptions.poweredBy?.companyUrl || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center"
                  >
                    {footerOptions.poweredBy?.text || 'Design & Development By '}

                    {footerOptions.poweredBy && (

                      <img
                        src={pitechLogo}
                        alt="PiTech Logo"
                        className="h-5 w-auto ml-2 transition-transform duration-300 group-hover:scale-110"
                      />

                    )}

                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;






// // before including desing & coloring & footer
// // src/components/Footer.js
// import React from 'react';
// import { Link } from 'react-router-dom';
// import { InstagramIcon, FacebookIcon, PhoneIcon, MailIcon, LinkedinIcon} from 'lucide-react';


// const Footer = () => {
//   const phoneNumber = "+1 (833) 333-3101";

//   return (
//     <footer className="bg-gray-900 text-white py-12 pb-0 mb-0">
//       <div className="container mx-auto px-4 grid md:grid-cols-4 gap-8">
//         <div>
//           <h4 className="text-xl font-bold mb-4">Bag & Box</h4>
//           <p className="text-gray-400">Professional custom packaging solutions for businesses across North America.</p>
//           {/* <img src='../images/logo.png' style={{height:150+"px"}}/> */}
//           <p className='mt-2'>
//           <Link to="/privacy-policy" className="hover:text-white transition">
//             Privacy Policy
//           </Link>
//           <span>
//           &nbsp;&nbsp; - &nbsp;&nbsp;
//           </span>
//           <Link to="/terms-conditions" className="hover:text-white transition">
//             Terms & Conditions
//           </Link></p>
//         </div>
//         <div>
//           <h4 className="text-xl font-bold mb-4">Quick Links</h4>
//           <ul className="space-y-0">
//             <li><Link to="/" className="hover:text-blue-500">Home</Link></li>
//             <li><Link to="/aboutUs" className="hover:text-blue-500">About Us</Link></li>
//             <li><Link to="/blog" className="hover:text-blue-500">Blog</Link></li>
//             <li><Link to="/products" className="hover:text-blue-500">Products</Link></li>
//             <li><Link to="/contactUs" className="hover:text-blue-500">Contact Us</Link></li>
//             <li><Link to="/support" className="hover:text-blue-500">Support</Link></li>
//           </ul>
//         </div>
//         <div>
//           <h4 className="text-xl font-bold mb-4">Contact</h4>
//           <p>
//             {/* ☏ Phone: {phoneNumber} */}
//             {/* {<PhoneIcon className="mr-2 h-5 w-5"/>} */}
//             ☏ Phone: <a href={`tel:${phoneNumber}`} className="">{phoneNumber}</a>

//            </p>
//           <p style={{marginTop:7+'px'}}>
//             ✉ Email: Info@bagbox.ca
//             {/* {<MailIcon className="mr-2 h-5 w-5"/>}  */}
//              </p>
//           <p style={{marginTop:7+'px'}}>
//            ⚲ Address: 102-1127 14th St W,<br/> North Vancouver, BC, V7P 1J9

//           </p>
//         </div>
//         <div>
//           <h4 className="text-xl font-bold mb-4">Follow Us</h4>
//           <div className="flex space-x-4">
//             <a href="https://www.instagram.com/bagbox.ca" className="text-gray-400 hover:text-white">{<InstagramIcon className="mr-2 h-5 w-5"/>}</a>
//             <a href="#" className="text-gray-400 hover:text-white">{<FacebookIcon className="mr-2 h-5 w-5"/>}</a>
//             <a href="#" className="text-gray-400 hover:text-white">{<LinkedinIcon className="mr-2 h-5 w-5"/>}</a>
//           </div>
//         </div>
//       </div>
//       {/* <div className="text-center mt-8 border-t border-gray-800 pt-4 pb-6">
//         <p>&copy; 2025 BagBox.ca | All Rights Reserved.<br/>
//         <img
//           src="/images/piTech-logo.png"
//           alt="PiTech Logo"
//           className="inline-block"
//           style={{ height: '25px', marginRight: '5px', verticalAlign: 'bottom' }}
//         />
//         <a href='https://pitech.ca' target='new' className="inline-flex items-center">
//          Design & Development By PiTech.ca</a>
//         </p>
//       </div> */}
//       <div className="mt-8 border-t border-gray-800 pt-4 pb-6">
//         <div className="container mx-auto px-4">
//           <div className="flex flex-col md:flex-row md:justify-between md:items-center text-center md:text-left">
//             <p className="mb-3 md:mb-0">&copy; 2025 BagBox.ca | All Rights Reserved.</p>
//             <div className="flex items-center justify-center md:justify-end">
//               <img
//                 src="/images/piTech-logo.png"
//                 alt=" "
//                 className="inline-block"
//                 style={{ height: '25px', marginRight: '5px', verticalAlign: 'bottom' }}
//               />
//               <a href='https://pitech.ca' target='new' className="inline-flex items-center">
//                 Design & Development By PiTech.ca
//               </a>
//             </div>
//           </div>
//         </div>
//       </div>
//     </footer>
//   );
// };

// export default Footer;